import { format } from 'date-fns'
import BorderColorIcon from '@mui/icons-material/BorderColor'
import DeleteIcon from '@mui/icons-material/Delete'

import { manageToast } from '../../components/Common/ManageToast'
import GenericButton from '../../components/GenericButton/GenericButton'

import { serverlessApi } from '../../utils/API'
import { renderEllipsisCell } from '../../utils/table'

import StatusCell from './StatusCell'
import InternalKeyCodeCell from './InternalKeyCodeCell'

export function omitEmptyValues (obj) {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      acc[key] = obj[key]
    }

    return acc
  }, {})
}

export const fetchCollection = (
  params,
  signal,
  setData,
  setTotal,
  setLoading,
  setTotalCount,
  setTotalLoading,
  methodKey
) => {
  return Promise.resolve()
  .then(() => {
    setLoading(true)
    setTotalLoading(true)
  })
  .then(() => omitEmptyValues(params))
  .then(params => {
    return serverlessApi[methodKey](signal ? { signal, params } : { params }).then(res => {
      setData(res?.data?.items || [])
      setTotal(res?.data?.meta?.total || 0)
      setTotalCount(res?.data?.meta?.total || 0)
      setLoading(false)
      setTotalLoading(false)
    })
  })
  .catch(e => {
    if (e?.name === 'CanceledError') {
      setLoading(true)
      setTotalLoading(true)
    } else {
      setLoading(false)
      setTotalLoading(false)
      manageToast(false, 'Server error. Something went wrong.')
    }
  })
}

export const createStatusOptions = statuses => {
  return statuses.map(option => {
    let rootClassNames = 'flex items-center font-medium'
    let pinClassNames = 'inline-block w-[6px] h-[6px] rounded-[50%] mr-1'

    if (option === 'Available') {
      rootClassNames += ' text-green-600'
      pinClassNames += ' bg-green-600'
    } else if (option === 'In Use') {
      rootClassNames += ' text-orange-600'
      pinClassNames += ' bg-orange-600'
    } else {
      rootClassNames += ' text-red-600'
      pinClassNames += ' bg-red-600'
    }

    return {
      label: (
        <div className={rootClassNames}>
          <span className={pinClassNames}/>
          {option}
        </div>
      ),
      value: option,
      key: option,
    }
  })
}

export const LOCKBOX_STATUS_OPTIONS = createStatusOptions(['Available', 'In Use', 'Not Available'])
export const INTERNAL_KEY_STATUS_OPTIONS = createStatusOptions(['Available', 'In Use', 'Not Available'])

export const createLockBoxCollectionColumns = (
  setSelectedLockBoxId, setEditModalOpen, setDeleteModalOpen,
  setDetailsModalOpen, refetch, profile
) => ([
  {
    title: 'LockBox Name',
    dataIndex: 'LockBoxName',
    render: (_, record) => (
      <div
        onClick={() => {
          setDetailsModalOpen(true)
          setSelectedLockBoxId(record._id)
        }}
        className="font-semibold text-[#007d7d] hover:cursor-pointer"
      >
        {record?.LockBoxName || '--'}
      </div>
    ),
    width: 250
  },
  {
    title: 'Property',
    dataIndex: 'PropertyID',
    render: (_, record) => {
      return record.property.internalListingName
    },
    align: 'center',
    width: 220
  },
  {
    title: 'Code',
    dataIndex: 'Code',
    render: (_, record) => record?.Code || '--',
    align: 'center',
    width: 140
  },
  {
    title: 'Lockbox Type',
    dataIndex: 'LockboxType',
    render: (_, record) => record?.LockboxType || '--', 
      align: 'center',
      width: 140
  },
  {
    title: 'Link',
    dataIndex: 'Link',
    render: (_, record) => record?.Link
      ? (
        <a
          className="hover:text-buttonTheme"
          rel="noopener noreferrer"
          href={record.Link}
          target="_blank"
        >
          {record.Link}
        </a>
      )
      : '--',
    align: 'center',
    width: 170
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (_, record) => (
      <StatusCell
        name={record?.LockBoxName}
        typeName="lockBox"
        requestKey="updateLockBoxStatus"
        statusOptions={LOCKBOX_STATUS_OPTIONS}
        disabled={!(profile?.updateLockBoxStatus || profile?.isAdmin)}
        refetch={refetch}
        {...record}
      />
    ),
    align: 'center',
    width: 160
  },
  {
    title: 'Comment',
    dataIndex: 'Comment',
    render: (_, { Comment }) => renderEllipsisCell(Comment),
    ellipsis: { showTitle: false },
    align: 'center',
    width: 170
  },
  {
    title: 'Updated At',
    dataIndex: 'updatedAt',
    render: (_, record) => record?.updatedAt
      ? format(new Date(record.updatedAt), 'dd/MM/yyyy')
      : '--',
    align: 'center',
    width: 150
  },
  (profile?.isAdmin || profile?.updateLockBox || profile?.deleteLockBox) && {
    title: 'Actions',
    dataIndex: '_id',
    render: (_, record) => (
      <div className='flex justify-center gap-2'>
        {(profile?.isAdmin || profile?.updateLockBox) && (
          <GenericButton
            label="Edit"
            className={`bg-labelGreen text-themeGreen border-themeGreen 
            hover:bg-white hover:!text-themeGreen hover:!border-themeGreen`}
            iconLeftAlign={true}
            icon={<BorderColorIcon style={{ fontSize: 15 }} />}
            onClick={() => {
              setEditModalOpen(true)
              setSelectedLockBoxId(record._id)
            }}
          />
        )}

        {(profile?.isAdmin || profile?.deleteLockBox) && (
          <GenericButton
            label="Delete"
            className={`bg-red-100 text-red-500 border-red-500 hover:bg-white 
            hover:!text-red-500 hover:!border-red-500`}
            iconLeftAlign={true}
            icon={<DeleteIcon style={{ fontSize: 15 }} />}
            onClick={() => {
              setDeleteModalOpen(true)
              setSelectedLockBoxId(record._id)
            }}
          />
        )}
      </div>
    ),
    align: 'center',
    width: 200
  }
].filter(Boolean))

export const createInternalKeysCollectionColumns = (
  setSelectedKeyId, setEditModalOpen, setDeleteModalOpen,
  setDetailsModalOpen, refetch, profile, activeKeyIds, setActiveKeyIds
) => ([
  {
    title: 'Internal Key Name',
    dataIndex: 'InternalKeyName',
    render: (_, record) => (
      <div
        onClick={() => {
          setDetailsModalOpen(true)
          setSelectedKeyId(record._id)
        }}
        className="font-semibold text-[#007d7d] hover:cursor-pointer"
      >
        {record?.InternalKeyName || '--'}
      </div>
    ),
    width: 240
  },
  {
    title: 'Property',
    dataIndex: 'PropertyID',
    render: (_, record) => record?.property?.internalListingName ||'--',
    align: 'center',
    width: 220
  },
  (profile?.isAdmin || profile?.readInternalKeyCode) && {
    title: 'Code',
    dataIndex: 'Code',
    render: (_, record) =>
      <InternalKeyCodeCell
        setActiveKeyIds={setActiveKeyIds}
        id={record._id}
      />,
    align: 'center',
    width: activeKeyIds.length ? 250 : 140
  },
  {
    title: 'Box',
    dataIndex: 'BoxType',
    render: (_, record) => record?.BoxType || '--',
    align: 'center',
    width: 100
  },
  {
    title: 'Internal Key Type',
    dataIndex: 'InternalKeyType',
    render: (_, record) => record?.InternalKeyType || '--',
    align: 'center',
    width: 150
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (_, record) => (
      <StatusCell
        name={record?.InternalKeyName}
        typeName="internal key"
        requestKey="updateInternalKeyStatus"
        statusOptions={INTERNAL_KEY_STATUS_OPTIONS}
        disabled={!(profile?.updateInternalKeyStatus || profile?.isAdmin)}
        refetch={refetch}
        {...record}
      />
    ),
    align: 'center',
    width: 160
  },
  {
    title: 'Updated At',
    dataIndex: 'updatedAt',
    render: (_, record) => record?.updatedAt
      ? format(new Date(record.updatedAt), 'dd/MM/yyyy')
      : '--',
    align: 'center',
    width: 150
  },
  (profile?.isAdmin || profile?.updateInternalKey || profile?.deleteInternalKey) && {
    title: 'Actions',
    dataIndex: '_id',
    render: (_, record) => (
      <div className='flex justify-center gap-2'>
        {(profile?.isAdmin || profile?.updateInternalKey) && (
          <GenericButton
            label="Edit"
            className={`bg-labelGreen text-themeGreen border-themeGreen 
            hover:bg-white hover:!text-themeGreen hover:!border-themeGreen`}
            iconLeftAlign={true}
            icon={<BorderColorIcon style={{ fontSize: 15 }} />}
            onClick={() => {
              setEditModalOpen(true)
              setSelectedKeyId(record._id)
            }}
          />
        )}

        {(profile?.isAdmin || profile?.deleteInternalKey) && (
          <GenericButton
            label="Delete"
            className={`bg-red-100 text-red-500 border-red-500 hover:bg-white 
            hover:!text-red-500 hover:!border-red-500`}
            iconLeftAlign={true}
            icon={<DeleteIcon style={{ fontSize: 15 }} />}
            onClick={() => {
              setDeleteModalOpen(true)
              setSelectedKeyId(record._id)
            }}
          />
        )}
      </div>
    ),
    align: 'center',
    width: 200
  }
].filter(Boolean))

export const createKeysCollectionColumns = () => ([
  {
    title: 'Key Name',
    dataIndex: 'KeyName',
    render: (_, record) => record?.KeyName || '--',
    className: 'font-semibold text-[#007d7d]'
  },
  {
    title: 'Last Movement',
    dataIndex: 'LastMovement',
    render: (_, record) => record?.LastMovement
      ? format(new Date(record.LastMovement), 'dd/MM/yyyy HH:mm')
      : '--',
    align: 'center'
  },
  {
    title: 'Current Status',
    dataIndex: 'CurrentStatus',
    render: (_, record) => record?.CurrentStatus || '--',
    align: 'center'
  },
  {
    title: 'Current User Name',
    dataIndex: 'CurrentUserName',
    render: (_, record) => record?.CurrentUserName || '--',
    align: 'center'
  }
])
